import { flagOriginWords } from '../utils/ticketFlow.js';
import { isKnownCommunicoStaff } from '../utils/sessionUser.js';
import { SUPPORT_EMAIL_PAGE_ENABLED } from '../utils/supportEmailSwitch.js';
import { spamBellWords } from '../utils/supportMailSpam.js';
import { oneLine } from '../utils/oneLine.js';
import { displayLabel } from '../utils/displayLabels.js';
import { runTitle } from '../utils/workflows.js';

// Stage 75 — shared notification display logic.
//
// The bell dropdown (cos-notification-bell) and the full page
// (cos-notifications-page) render the same rows, so the kind→display
// derivation, the recency grouping, and the filter-category mapping all
// live here instead of being duplicated. Display text + link are DERIVED
// from kind+payload client-side (the server stores only kind + payload).

export interface NotificationRow {
	id: number;
	user_id?: number;
	kind: string;
	payload: Record<string, unknown>;
	read_at?: string | null;
	dismissed_at?: string | null;
	created_at: string;
}

export interface NotificationDisplay {
	/** Source glyph (a cos-icon name). */
	icon: string;
	/** Primary line. */
	label: string;
	/** Optional secondary line. */
	subtitle: string;
	/** In-app link target (or '#' when none derivable). */
	href: string;
	/**
	 * Feedback #10 — set for the feedback.* kinds, which have no in-app
	 * record page to link to (href stays '#'). Clicking should open the
	 * feedback widget focused on this report (commands/feedbackReport.ts)
	 * instead of navigating — the real "actionable" destination, reusing
	 * the mechanism already built for the re-test email link.
	 */
	feedbackReportId?: number;
}

function feedbackReportId(p: Record<string, unknown>): number | undefined {
	return typeof p.reportId === 'number' ? p.reportId : undefined;
}

function str(v: unknown, fallback = ''): string {
	return typeof v === 'string' && v ? v : fallback;
}

/**
 * Feedback #10 — the task kinds' scope_type/scope_id (model.TaskNotificationPayload)
 * name the record the task belongs to, if any. A personal/unscoped task
 * (scope_type '') has no owning record to deep-link to, so it falls back
 * to the general task list rather than a dead '#'.
 */
function taskNotificationHref(p: Record<string, unknown>): string {
	const scopeType = str(p.scope_type);
	const scopeID = p.scope_id;
	if (typeof scopeID !== 'number' || !scopeType) return '/tasks';
	switch (scopeType) {
		case 'ticket':
			return `/tickets/${scopeID}#tasks`;
		case 'partner':
			return `/partners/${scopeID}#tasks`;
		case 'contact':
			return `/contacts/${scopeID}`;
		case 'deal':
			return `/deals/${scopeID}`;
		case 'project':
			return `/projects/${scopeID}#todos`;
		default:
			return '/tasks';
	}
}

/**
 * The six user-toggleable source kinds (Stage 75). The order is the order
 * the preferences modal renders its checkboxes in.
 */
export const TOGGLEABLE_KINDS = [
	'chat_mention',
	'mention_in_comment',
	'mention_in_partner_activity',
	'mention_anchored_in_conversation',
	'watched_ticket_comment',
	'watched_ticket_status_change',
	'snooze_released',
	// A library raised a request in COS (support staff). Mutable like the other ticket notices.
	'ticket_raised_by_library',
	// The reply you wrote could not be emailed (contract section 15 e). Mutable like the other ticket notices.
	'ticket_reply_email_failed',
	// Engineering finished, or reopened, the work a support ticket waits on (staff on that
	// support ticket). Mutable on the server like the other ticket notices.
	'ticket_link_engineering_done',
	'ticket_link_engineering_reopened',
	// Engineering handed back a ticket you raised (the engineering ticket's own reporter).
	'engineering_handed_back',
	// A ticket you raised is now with you: an agent's question, a test, or a release to approve.
	'engineering_with_reporter',
	// An agent-driven engineering ticket needs a person (product, 26 October 2026).
	'engineering_flag_raised',
	// Emails that need a person (the email server's bell to support staff). Staff only: see STAFF_ONLY_KINDS.
	'support_mail_needs_a_person',
	// Emails Gmail filed as spam that nobody has looked at (every other day at most). Staff only.
	'support_mail_spam_waiting',
	// Comms Hub Phase 6a — Roster's staff notices. Muting one here mutes
	// only the bell; email/SMS are a separate axis (per-channel), which is
	// the point of the two rails.
	'roster_shift_published',
	'roster_shift_updated',
	'roster_shift_cancelled',
	'roster_claim_decided',
	'roster_leave_decided',
	'roster_swap_offered',
	'roster_swap_decided',
	// roster W4c — the open-shift broadcast, fanned to every eligible
	// worker (not one assignee). Same rail, same muteability.
	'roster_open_shift_posted',
	// roster ledger R-F — Targeted Ask, one named worker. Same rail, same
	// muteability.
	'roster_shift_asked',
	// FR-64 — the manager-composed weekly exception digest. Same rail,
	// same muteability; the words are the manager's own, not a template.
	'roster_weekly_digest',
	// Staff feedback / issue-capture platform — reporter-facing notices.
	'feedback.message',
	'feedback.status',
	'feedback.retest_request',
	'feedback.verified',
	// Action Center (Part 6.1.5) — bell-only in this phase; the email channel
	// arrives with AC3b's kind -> {bell, email} preference extension.
	'action_center_item',
	// Attend attendance nag (AC6, columbus-attend-spec.md item 2) — a staff
	// member who tracks this from the Needs Attendance filter instead is
	// reasonable, same reasoning as Roster's kinds above.
	'attend_attendance_nag',
	// Circulate fund alert (F27) - the bell and email only; the fund stays in
	// the Action Center Funds tab whatever is muted.
	'circulate_fund_low',
] as const;

export type ToggleableKind = (typeof TOGGLEABLE_KINDS)[number];

/** Human-readable labels for the preferences checkboxes. */
export const KIND_PREF_LABELS: Record<ToggleableKind, string> = {
	chat_mention: 'Chat mentions',
	mention_in_comment: 'Ticket comment mentions',
	mention_in_partner_activity: 'Partner activity mentions',
	mention_anchored_in_conversation: 'Conversation anchors',
	watched_ticket_comment: 'Watched ticket replies',
	watched_ticket_status_change: 'Ticket status changes',
	ticket_raised_by_library: 'New requests from a library',
	ticket_link_engineering_done: 'Engineering finished the work a ticket waits on',
	ticket_link_engineering_reopened: 'Engineering reopened the work a ticket waits on',
	engineering_handed_back: 'Engineering handed back a ticket you raised',
	engineering_with_reporter: 'A ticket you raised is with you',
	engineering_flag_raised: 'An engineering ticket needs a person',
	ticket_reply_email_failed: 'A reply of yours was not emailed',
	support_mail_needs_a_person: 'Emails that need a person',
	support_mail_spam_waiting: 'Emails filed as spam',
	snooze_released: 'Snooze releases',
	roster_shift_published: 'New shifts on your roster',
	roster_shift_updated: 'Changes to your shifts',
	roster_shift_cancelled: 'Cancelled shifts',
	roster_claim_decided: 'Shift claim decisions',
	roster_leave_decided: 'Leave decisions',
	roster_swap_offered: 'Shift swaps offered to you',
	roster_swap_decided: 'Shift swap decisions',
	roster_open_shift_posted: 'Open shifts you could pick up',
	roster_shift_asked: 'A manager asked you about a shift',
	roster_weekly_digest: 'The weekly roster digest',
	// Staff feedback / issue-capture platform — reporter-facing notices.
	'feedback.message': 'New replies on your feedback reports',
	'feedback.status': 'Status changes on your feedback reports',
	'feedback.retest_request': 'Re-test requests',
	'feedback.verified': 'Verified fixes',
	action_center_item: 'Action Center: new item',
	attend_attendance_nag: 'Attend: attendance not marked',
	circulate_fund_low: 'Circulate: a fund is running low',
};

/**
 * Kinds only Communico staff are offered to mute. The server never sends them to a library login; the
 * preferences box also leaves them out for anyone not known to be staff.
 */
export const STAFF_ONLY_KINDS: readonly ToggleableKind[] = [
	'support_mail_needs_a_person',
	'support_mail_spam_waiting',
	// Engineering tickets are never named to a library login, so neither are the notices about them.
	'ticket_link_engineering_done',
	'ticket_link_engineering_reopened',
	'engineering_handed_back',
	'engineering_with_reporter',
	'engineering_flag_raised',
];

/**
 * Is this row for this login to see? A staff-only kind is never drawn for anyone not known to be
 * Communico staff, even if a server sent it by mistake (a library login never learns that
 * engineering tickets exist). The bell and the notifications page both filter through it.
 */
export function isNotificationVisible(n: NotificationRow): boolean {
	// The spam reminder only points at the Support email page: with the page switched off it is not drawn at all.
	if (n?.kind === 'support_mail_spam_waiting' && !SUPPORT_EMAIL_PAGE_ENABLED) return false;
	return isKnownCommunicoStaff() || !((STAFF_ONLY_KINDS as readonly string[]).includes(n?.kind));
}

/** Derive icon + label + subtitle + link from a notification's kind+payload. */
// The words for a status in a watched-ticket notice. The server sends a library the
// partner status and its wording (`status_label`); Communico staff keep reading the
// stored status. For anyone else, with no wording sent, there is no status text.
function watchedStatusWords(p: Record<string, unknown>): string {
	// Communico staff read the stored status in the staff words, whatever else the payload
	// carries: the library's wording is for the library.
	// The staff payload names the new status `new_status` (contract 12 d: staff
	// payloads are as they were); `status` is the library's name for the field.
	// An engineering ticket's step comes in the table's words from the server (`new_status_words`); the stored
	// status read through the support words is for a support ticket and for a row from before the field.
	if (isKnownCommunicoStaff()) return str(p.new_status_words) || displayLabel('TicketStatus', str(p.new_status) || str(p.status));
	return str(p.status_label);
}

export function notificationDisplay(n: NotificationRow): NotificationDisplay {
	const p = (n.payload || {}) as Record<string, unknown>;
	switch (n.kind) {
		// --- Stage 75 source kinds ---------------------------------------
		case 'chat_mention':
			return {
				icon: 'users',
				label: `${str(p.author, 'Someone')} mentioned you in #${str(p.conversation_name, 'a channel')}`,
				subtitle: '',
				href: '/conversations',
			};
		case 'mention_anchored_in_conversation':
			return {
				icon: 'users',
				label: `${str(p.actor_name, 'Someone')} anchored you to ${p.source_type ? `a ${str(p.source_type)} conversation` : 'a conversation'}`,
				subtitle: str(p.conversation_name) || str(p.snippet),
				href: '/conversations',
			};
		case 'mention_in_comment':
			return {
				icon: 'ticket',
				// Stage 81 — payload field is author_name (the backend
				// MentionInCommentPayload), now populated, so the bell reads
				// "Marcie mentioned you" not "Someone". An internal-note
				// mention deep-links to the #internal-notes section so the
				// click lands on the conversation, not the ticket top.
				label: `${str(p.author_name, 'Someone')} mentioned you in ${p.is_internal ? 'an internal note' : 'a comment'}`,
				subtitle: str(p.ticket_title),
				href:
					typeof p.ticket_id === 'number'
						? `/tickets/${p.ticket_id}${p.is_internal ? '#internal-notes' : ''}`
						: '#',
			};
		case 'mention_in_partner_activity':
			return {
				icon: 'user',
				label: `${str(p.actor_name, 'Someone')} mentioned you in a ${str(p.activity_type, 'activity')}`,
				subtitle: str(p.partner_name),
				href: typeof p.partner_id === 'number' ? `/partners/${p.partner_id}` : '#',
			};
		case 'watched_ticket_comment':
			return {
				icon: 'ticket',
				label: `New reply on a watched ticket`,
				subtitle: str(p.ticket_title),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'watched_ticket_status_change':
			return {
				icon: 'flag',
				// A library reads the wording the server sent with the partner status (or
				// nothing); only Communico staff read the stored status as it is.
				label: `Status changed${watchedStatusWords(p) ? ` to ${watchedStatusWords(p)}` : ''} on a watched ticket`,
				subtitle: str(p.ticket_title),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		// Engineering finished the work this support ticket is linked to. The
		// support ticket's status has NOT moved: this only says it is ready to be
		// resolved. The reference comes from the payload; with none, the title
		// alone names the work.
		case 'ticket_link_engineering_done': {
			const work = str(p.engineering_ticket_ref) || str(p.engineering_ticket_title, 'the linked engineering ticket');
			// The server says which hand back it is (`hand_back_words`) and what to do next
			// (`next_step`, empty for three of the four); those words are drawn as sent. A row
			// from before they existed has neither and keeps the old line.
			const words = str(p.hand_back_words);
			const next = str(p.next_step);
			return {
				icon: 'check',
				label: words
					? `Engineering has handed back ${work}: ${words}.${next ? ` ${next}` : ''}`
					: `Engineering has finished ${work}. This support ticket is ready to be resolved.`,
				subtitle: str(p.ticket_title),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		}
		// Engineering reopened the work this support ticket is linked to, after the
		// people on it were told it was finished. The support ticket's status has NOT
		// moved, even when it is resolved or closed.
		case 'ticket_link_engineering_reopened': {
			const work = str(p.engineering_ticket_ref) || str(p.engineering_ticket_title, 'the linked engineering ticket');
			return {
				icon: 'rotate-ccw',
				label: `Engineering has reopened ${work}. This support ticket may not be ready to be resolved.`,
				subtitle: str(p.ticket_title),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		}
		// The engineering ticket's own reporter is told it was handed back. Unlike the two above it
		// opens the ENGINEERING ticket. The words (`hand_back_words`, `next_step`) are the server's.
		case 'engineering_handed_back': {
			const ref = str(p.engineering_ticket_ref);
			const title = str(p.engineering_ticket_title);
			const work = ref || title || 'a ticket you raised';
			const words = str(p.hand_back_words);
			const next = str(p.next_step);
			return {
				icon: 'check',
				label: `${work} was handed back${words ? `: ${words}` : ''}.${next ? ` ${next}` : ''}`,
				subtitle: ref ? title : '',
				href: typeof p.engineering_ticket_id === 'number' ? `/tickets/${p.engineering_ticket_id}` : '#',
			};
		}
		// A ticket the person raised is with them (20 October 2026): the crew asked a question,
		// it is ready to test, or ready to release. The words (`next_step`) are the server's; it
		// opens the engineering ticket.
		case 'engineering_with_reporter': {
			const ref = str(p.ticket_ref);
			const title = str(p.ticket_title);
			const work = ref || title || 'A ticket you raised';
			const next = str(p.next_step);
			return {
				icon: 'user',
				label: `${work} is with you${next ? `: ${next}` : ''}`,
				subtitle: ref ? title : '',
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		}
		// An agent-driven engineering ticket needs a person (26 October 2026): product picks an
		// engineer. The line names the ticket and where the flag came from; it opens the ticket.
		case 'engineering_flag_raised': {
			const ref = str(p.ticket_ref);
			const title = str(p.ticket_title);
			const work = ref || title || 'An engineering ticket';
			const origin = flagOriginWords(str(p.origin));
			const reason = str(p.reason);
			return {
				icon: 'flag',
				label: `${work} needs a person${origin ? `: flagged by ${origin}` : ''}`,
				subtitle: reason || (ref ? title : ''),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		}
		// A library raised a ticket in COS (staff on the support desk only). The line
		// names the library, the subtitle the reference and the subject; it opens the
		// ticket. With no library name it says "from a library".
		case 'ticket_raised_by_library': {
			const ref = str(p.ticket_ref);
			const title = str(p.ticket_title);
			return {
				icon: 'ticket',
				label: `New request from ${str(p.partner_name, 'a library')}`,
				subtitle: [ref, title].filter(Boolean).join(' '),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		}
		// The person who replied is told their reply was not emailed. The line names the
		// reference, the server's own `label` says what happened, and it opens the ticket
		// (the page cannot open at one reply from an address, and the payload names none).
		case 'ticket_reply_email_failed': {
			const ref = str(p.ticket_ref);
			const title = str(p.ticket_title);
			return {
				icon: 'mail',
				label: ref ? `Your reply on ${ref} was not emailed` : title ? `Your reply on ${title} was not emailed` : 'Your reply was not emailed',
				subtitle: str(p.label),
				// The reply itself when the server names it (`comment_id`): the ticket page scrolls to it.
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}${typeof p.comment_id === 'number' && p.comment_id > 0 ? `?comment=${p.comment_id}` : ''}` : '#',
			};
		}
		// The email server's bell to support staff. It has three meanings (`reason`: held, unreadable, readable_again,
		// and any the screens do not know), each with its own sentence: the server's `message` is printed as sent, as one
		// line, and the screens build no sentence of their own from `count`. It opens the Support email page by the
		// screens' own route (the payload's `path` is never followed), and only for known staff and only while the page exists.
		case 'support_mail_needs_a_person': {
			return {
				icon: 'mail',
				label: oneLine(str(p.message)) || 'Emails need a person',
				subtitle: '',
				href: SUPPORT_EMAIL_PAGE_ENABLED && isKnownCommunicoStaff() ? '/tickets/support-email' : '#',
			};
		}
		// The reminder, every other day, that emails Gmail filed as spam are waiting. The sentence is built here from the
		// payload's `unlooked` (one, many, or none readable); the link is the screens' own route, never the payload's.
		case 'support_mail_spam_waiting': {
			return {
				icon: 'mail',
				label: spamBellWords(p.unlooked),
				subtitle: '',
				href: SUPPORT_EMAIL_PAGE_ENABLED && isKnownCommunicoStaff() ? '/tickets/support-email#spam' : '#',
			};
		}
		case 'snooze_released':
			return {
				icon: 'clock',
				label: 'A snoozed item is back',
				subtitle: str(p.title),
				href: '/inbox',
			};
		// Action Center (Part 6.1.5) — a scheduler scan diffs each user's
		// current queue against notification_sent_log and dispatches this
		// once per newly-raised item. The item itself always raises
		// regardless of mute state (D1/O5 — muting hides the ping, never
		// the work); this is purely the "you were told" rail.
		case 'action_center_item':
			return {
				icon: 'list',
				label: str(p.title, 'A new item needs your action'),
				subtitle: str(p.source_label) || str(p.source),
				href: '/action-center',
			};
		// Circulate fund alert (F27): one notice per fund per crossing. The
		// server writes the words (title, body) and the link into the fund.
		case 'circulate_fund_low':
			return {
				icon: 'dollar-sign',
				label: str(p.title, 'A fund is running low'),
				subtitle: str(p.body),
				href: str(p.link, '/action-center'),
			};
		// Attend attendance nag (AC6, columbus-attend-spec.md item 2) — fires
		// once per occurrence, AttendAttendanceNagGraceDays after it ended
		// with attendance still unmarked (read straight from
		// post_event_data, independent of the display setting — D2/O2).
		case 'attend_attendance_nag':
			return {
				icon: 'clipboard-list',
				label: `Attendance not yet marked for "${str(p.title, 'an event')}"`,
				subtitle: 'Needs attendance',
				href: str(p.href, '/attend/events'),
			};
		// --- Big Arc Sub-arc 1: unified task notifications ---------------
		// Feedback #10 — the backend's TaskNotificationPayload already
		// carries scope_type/scope_id for deep-linking (model/notification.go),
		// but this switch had no case for the four task kinds, so they fell
		// through to the unhandled-kind default (href '#') — tapping one in
		// the bell went nowhere. Built from the payload the dispatch sites
		// already send; no backend change needed.
		case 'task_assigned':
			return {
				icon: 'check',
				label: `${str(p.actor_name, 'Someone')} assigned you "${str(p.title, 'a task')}"`,
				subtitle: '',
				href: taskNotificationHref(p),
			};
		case 'task_completed':
			return {
				icon: 'check',
				label: `${str(p.actor_name, 'Someone')} completed "${str(p.title, 'a task')}"`,
				subtitle: '',
				href: taskNotificationHref(p),
			};
		case 'task_reminder':
			return {
				icon: 'clock',
				label: `Reminder: "${str(p.title, 'a task')}" is due`,
				subtitle: '',
				href: taskNotificationHref(p),
			};
		case 'task_alarm':
			return {
				icon: 'clock',
				label: `Overdue: "${str(p.title, 'a task')}"`,
				subtitle: str(p.overdue_hint) ? `${str(p.overdue_hint)} overdue` : '',
				href: taskNotificationHref(p),
			};
		// --- Pre-existing kinds (Stage 38a / 67a) ------------------------
		case 'ticket_assigned_to_me':
			return {
				icon: 'ticket',
				label: `Ticket "${str(p.ticket_title, 'untitled')}" assigned to you`,
				subtitle: '',
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'comment_added_on_my_ticket':
			return {
				icon: 'ticket',
				label: `New comment on "${str(p.ticket_title, 'your ticket')}"`,
				subtitle: '',
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'contact_completeness_alert':
			return {
				icon: 'user',
				label: `Contact list incomplete for ${str(p.partner_name, 'a partner')}`,
				subtitle: '',
				href: typeof p.partner_id === 'number' ? `/partners/${p.partner_id}` : '#',
			};
		// --- Feedback #306 — scheduler-dispatched kinds with no case here
		// fell through to the unhandled-kind default (href '#'), so clicking
		// one marked it read and went nowhere — same class of bug Feedback
		// #10 fixed for feedback.* rows, just for a different family of
		// kinds (internal/scheduler/scanSLA, scanStaleDeals, scanModuleStall,
		// scanAnnualReviews) plus the two watcher-channel kinds
		// (TicketNotifier.AssigneeChanged/AttachmentAdded) and escalation
		// firing. Built from the payloads those dispatch sites already send;
		// no backend change needed.
		case 'ticket_sla_warning':
			return {
				icon: 'clock',
				label: `SLA warning (75%) on "${str(p.ticket_title, 'a ticket')}"`,
				subtitle: '',
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'ticket_sla_alert':
			return {
				icon: 'alert-triangle',
				label: `SLA alert (90%) on "${str(p.ticket_title, 'a ticket')}"`,
				subtitle: '',
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'ticket_sla_breach':
			return {
				icon: 'flag',
				label: `SLA breached on "${str(p.ticket_title, 'a ticket')}"`,
				subtitle: '',
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'stale_deal':
			return {
				icon: 'dollar-sign',
				label: `"${str(p.deal_name, 'A deal')}" has stalled in ${str(p.stage, 'its stage')}`,
				subtitle: typeof p.days_in_stage === 'number' ? `${p.days_in_stage}d with no change` : '',
				href: typeof p.deal_id === 'number' ? `/deals/${p.deal_id}` : '#',
			};
		case 'module_stall':
			return {
				icon: 'package',
				label: `${str(p.module_name, 'A module')} has stalled at ${str(p.partner_name, 'a partner')}`,
				subtitle: typeof p.days_stalled === 'number' ? `${p.days_stalled}d with no activity` : '',
				href: typeof p.partner_id === 'number' ? `/partners/${p.partner_id}#modules` : '#',
			};
		case 'annual_review_due':
			return {
				icon: 'calendar',
				label: `Annual review due for ${str(p.partner_name, 'a partner')}`,
				subtitle: typeof p.days_until === 'number' ? `${p.days_until}d away` : '',
				href: typeof p.partner_id === 'number' ? `/partners/${p.partner_id}#reviews` : '#',
			};
		case 'watched_ticket_assignee_change':
			return {
				icon: 'user',
				label: `Assignee changed on a watched ticket`,
				subtitle: str(p.ticket_title),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'watched_ticket_attachment':
			return {
				icon: 'paperclip',
				label: `New attachment on a watched ticket`,
				subtitle: str(p.ticket_title) || str(p.attachment_name),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'escalation_fired':
			return {
				icon: 'alert-triangle',
				label: str(p.message, 'An escalation rule fired'),
				subtitle: str(p.rule_name),
				href: typeof p.ticket_id === 'number' ? `/tickets/${p.ticket_id}` : '#',
			};
		case 'chat_dm':
			return {
				icon: 'users',
				label: `${str(p.author, 'Someone')}: ${str(p.snippet, 'new message')}`,
				subtitle: '',
				href: '/conversations',
			};
		case 'chat_reminder':
			return {
				icon: 'clock',
				label: `Reminder: ${str(p.snippet, 'a chat message')}`,
				subtitle: '',
				href: '/conversations',
			};
		case 'chat_meet':
			return {
				icon: 'users',
				label: `${str(p.author, 'Someone')} started a Meet${p.conversation_name ? ` in #${str(p.conversation_name)}` : ''}`,
				subtitle: 'Click to open chat',
				href: '/conversations',
			};
		case 'timeoff_requested':
			return {
				icon: 'calendar',
				label: `${str(p.requester_name, 'Someone')} requested ${str(p.leave_type, 'time off')}`,
				subtitle: [str(p.start_date), p.days ? `${p.days}d` : ''].filter(Boolean).join(' · '),
				href: '/timeoff',
			};
		case 'timeoff_decided':
			return {
				icon: 'calendar',
				label: `Your ${str(p.leave_type, 'time off')} request was ${str(p.status, 'decided')}`,
				subtitle: p.decider_name ? `by ${str(p.decider_name)}` : '',
				href: '/timeoff',
			};
		// --- Comms Hub Phase 6a: Roster staff notices --------------------
		// All seven share one payload (title/body/link) because the same
		// notice also goes out by email and SMS — the copy is rendered
		// once, producer-side, and every surface reads the same words.
		case 'roster_shift_published':
		case 'roster_shift_updated':
		case 'roster_shift_cancelled':
		case 'roster_claim_decided':
		case 'roster_leave_decided':
		case 'roster_swap_offered':
		case 'roster_swap_decided':
		case 'roster_open_shift_posted':
		case 'roster_shift_asked':
		case 'roster_weekly_digest':
			return {
				icon: rosterNoticeIcon(n.kind),
				label: str(p.title, 'Roster update'),
				subtitle: str(p.body),
				href: str(p.link, '/roster'),
			};
		// --- Staff feedback / issue-capture platform ---------------------
		// Reporter-facing notices. No in-app route resolves an individual
		// report (it lives inside the widget's My Reports tab, not a page),
		// so href stays '#' — but feedbackReportId gives the click handler
		// something real to do: open the widget straight to the report
		// (see the interface doc above).
		case 'feedback.message':
			return { icon: 'bug', label: `New reply on "${str(p.title, 'your feedback report')}"`, subtitle: '', href: '#', feedbackReportId: feedbackReportId(p) };
		case 'feedback.status':
			// A decline (round 5) carries its required reason in the payload —
			// surface it as the subtitle so the bell itself answers "why"
			// without a click-through (there's nowhere to click through to yet).
			return {
				icon: 'bug',
				label: `"${str(p.title, 'Your feedback report')}" is now ${str(p.status, 'updated')}`,
				subtitle: p.status === 'declined' ? str(p.reason) : '',
				href: '#',
				feedbackReportId: feedbackReportId(p),
			};
		case 'feedback.retest_request':
			return { icon: 'bug', label: `Please re-test "${str(p.title, 'your feedback report')}"`, subtitle: '', href: '#', feedbackReportId: feedbackReportId(p) };
		case 'feedback.verified':
			return { icon: 'check', label: `"${str(p.title, 'Your feedback report')}" was verified fixed`, subtitle: '', href: '#', feedbackReportId: feedbackReportId(p) };
		// The engine or an admin paused a workflow run of yours (PRD 7.12): why, and a link to the run.
		case 'workflow_run_paused': {
			const runID = str(p.run_id);
			const number = typeof p.number === 'number' ? p.number : null;
			const name = number !== null ? runTitle(number, str(p.workflow, 'a workflow')) : 'A workflow run';
			return {
				icon: 'alert-triangle',
				label: `${name} paused`,
				subtitle: p.reason ? displayLabel('WorkflowRunReason', str(p.reason)) : '',
				href: /^[0-9a-f-]{36}$/i.test(runID) ? `/workflow-runs/${runID}` : '/workflow-runs',
			};
		}
		// A workflow asks you to approve or answer something (agent workflows W3).
		case 'workflow_request': {
			const id = typeof p.request_id === 'number' ? p.request_id : 0;
			return {
				icon: 'user-check',
				label: `${str(p.workflow, 'A workflow')} asks you: ${str(p.title, 'an answer')}`,
				subtitle: '',
				href: id > 0 ? `/workflow-inbox?request=${id}` : '/workflow-inbox',
			};
		}
		default:
			return { icon: 'bell', label: n.kind.replace(/_/g, ' '), subtitle: '', href: '#' };
	}
}

/**
 * Roster notices carry meaning in the glyph as well as the words — a
 * cancelled shift and a new one shouldn't look alike at a glance.
 *
 * The decision kinds (claim, leave, swap) deliberately DON'T get a tick:
 * they cover approvals and declines alike, so a glyph that implies an
 * outcome would be wrong half the time. Only cancellation, which has one
 * meaning, gets a loaded icon.
 */
function rosterNoticeIcon(kind: string): string {
	switch (kind) {
		case 'roster_shift_cancelled':
			return 'x';
		case 'roster_shift_updated':
			return 'clock';
		case 'roster_leave_decided':
			return 'clipboard-list';
		case 'roster_swap_offered':
		case 'roster_swap_decided':
			return 'arrow-left-right';
		case 'roster_open_shift_posted':
			return 'megaphone';
		case 'roster_shift_asked':
			return 'message-circle';
		case 'roster_weekly_digest':
			return 'mail';
		default:
			return 'calendar';
	}
}

// --- Recency grouping ------------------------------------------------------

export type RecencyGroup = 'Today' | 'Earlier this week' | 'Older';
export const RECENCY_ORDER: RecencyGroup[] = ['Today', 'Earlier this week', 'Older'];

/** Bucket a notification by its created_at relative to `now`. */
export function recencyGroup(iso: string, now: Date = new Date()): RecencyGroup {
	const t = Date.parse(iso);
	if (isNaN(t)) return 'Older';
	const startOfToday = new Date(now);
	startOfToday.setHours(0, 0, 0, 0);
	if (t >= startOfToday.getTime()) return 'Today';
	const weekAgo = startOfToday.getTime() - 6 * 86400_000;
	if (t >= weekAgo) return 'Earlier this week';
	return 'Older';
}

/**
 * Group rows into ordered recency buckets, newest-first within each group.
 * Returns only non-empty groups, in RECENCY_ORDER.
 */
export function groupByRecency(
	rows: NotificationRow[],
	now: Date = new Date(),
): Array<{ group: RecencyGroup; rows: NotificationRow[] }> {
	const buckets: Record<RecencyGroup, NotificationRow[]> = {
		Today: [],
		'Earlier this week': [],
		Older: [],
	};
	for (const r of rows) buckets[recencyGroup(r.created_at, now)].push(r);
	for (const g of RECENCY_ORDER) {
		buckets[g].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
	}
	return RECENCY_ORDER.filter((g) => buckets[g].length > 0).map((g) => ({ group: g, rows: buckets[g] }));
}

// --- Page filter chips -----------------------------------------------------

export type NotificationFilter = 'all' | 'unread' | 'mentions' | 'ticket' | 'snooze';

export const FILTER_CHIPS: Array<{ key: NotificationFilter; label: string }> = [
	{ key: 'all', label: 'All' },
	{ key: 'unread', label: 'Unread' },
	{ key: 'mentions', label: 'Mentions' },
	{ key: 'ticket', label: 'Ticket activity' },
	{ key: 'snooze', label: 'Snooze releases' },
];

const MENTION_KINDS = new Set([
	'chat_mention',
	'mention_in_comment',
	'mention_in_partner_activity',
	'mention_anchored_in_conversation',
]);
const TICKET_KINDS = new Set([
	'watched_ticket_comment',
	'watched_ticket_status_change',
	'ticket_link_engineering_done',
	'ticket_link_engineering_reopened',
	'engineering_handed_back',
	'engineering_with_reporter',
	'engineering_flag_raised',
	'ticket_raised_by_library',
	'ticket_reply_email_failed',
	'support_mail_needs_a_person',
	'support_mail_spam_waiting',
	'ticket_assigned_to_me',
	'comment_added_on_my_ticket',
]);

/** Does a row pass the given filter chip? (client-side filter for v1). */
export function matchesFilter(n: NotificationRow, filter: NotificationFilter): boolean {
	switch (filter) {
		case 'all':
			return true;
		case 'unread':
			return !n.read_at;
		case 'mentions':
			return MENTION_KINDS.has(n.kind);
		case 'ticket':
			return TICKET_KINDS.has(n.kind);
		case 'snooze':
			return n.kind === 'snooze_released';
	}
}
